import React from "react";
import { Textarea, Group, ActionIcon, Stack, Paper, Text, Button } from "@mantine/core";
import {
    IconBold,
    IconItalic,
    IconList,
    IconListCheck,
    IconH1,
    IconH2
} from "@tabler/icons-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const MarkdownEditor = ({ value, onChange, placeholder, minRows = 10, disabled = false, isEditing = false, onToggleEdit, onFocus, onBlur }) => {
    const insertMarkdown = (before, after = "") => {
        const textarea = document.querySelector('textarea[data-markdown-editor="true"]');
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = value.substring(start, end);
        const newText = value.substring(0, start) + before + selectedText + after + value.substring(end);
        
        onChange(newText);
        
        // Restore cursor position
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + before.length, start + before.length + selectedText.length);
        }, 0);
    };

    const formatButtons = [
        { icon: IconBold, label: "Bold", action: () => insertMarkdown("**", "**") },
        { icon: IconItalic, label: "Italic", action: () => insertMarkdown("*", "*") },
        { icon: IconH1, label: "Heading 1", action: () => insertMarkdown("# ") },
        { icon: IconH2, label: "Heading 2", action: () => insertMarkdown("## ") },
        { icon: IconList, label: "List", action: () => insertMarkdown("- ") },
        { icon: IconListCheck, label: "Checklist", action: () => insertMarkdown("- [ ] ") }
    ];

    // Custom renderer for checkboxes
    const components = {
        input: ({ checked, ...props }) => {
            if (props.type === 'checkbox') {
                return (
                    <input
                        type="checkbox"
                        checked={checked}
                        readOnly={disabled}
                        onClick={(e) => e.stopPropagation()} // Prevent toggling edit mode
                        onChange={(e) => {
                            if (disabled) return;
                            
                            // Find all checkboxes in the viewer to get the index of the clicked one
                            const viewer = e.target.closest('.markdown-viewer');
                            if (!viewer) return;
                            
                            const allCheckboxes = Array.from(viewer.querySelectorAll('input[type="checkbox"]'));
                            const index = allCheckboxes.indexOf(e.target);
                            
                            if (index === -1) return;

                            // Find and toggle the checkbox in markdown
                            const lines = value.split('\n');
                            let checkboxCount = 0;
                            const newLines = lines.map(line => {
                                // Match standard markdown checkbox: - [ ] or * [ ] or + [ ]
                                if (line.match(/^\s*[-*+]\s+\[[ xX]\]/)) {
                                    if (checkboxCount === index) {
                                        checkboxCount++;
                                        return line.includes('[ ]') 
                                            ? line.replace('[ ]', '[x]')
                                            : line.replace(/\[[xX]\]/, '[ ]');
                                    }
                                    checkboxCount++;
                                }
                                return line;
                            });
                            onChange(newLines.join('\n'));
                        }}
                        style={{ cursor: disabled ? 'default' : 'pointer', marginRight: '8px' }}
                    />
                );
            }
            return <input {...props} />;
        }
    };

    // Calculate checklist progress
    const calculateProgress = () => {
        const checkboxes = value.match(/- \[([ x])\]/g) || [];
        if (checkboxes.length === 0) return null;
        
        const checked = checkboxes.filter(cb => cb.includes('[x]')).length;
        const total = checkboxes.length;
        const percentage = Math.round((checked / total) * 100);
        
        return { checked, total, percentage };
    };

    const progress = calculateProgress();

    if (!isEditing) {
        return (
            <Stack gap="xs">
                {progress && (
                    <Paper p="xs" withBorder bg="var(--mantine-color-blue-light)">
                        <Group justify="space-between">
                            <Group gap="xs">
                                <Text size="sm" fw={500} c="blue">
                                    Checklist Progress: {progress.checked}/{progress.total}
                                </Text>
                            </Group>
                            <Text size="sm" fw={700} c="blue">
                                {progress.percentage}%
                            </Text>
                        </Group>
                    </Paper>
                )}
                <Paper 
                    p="md" 
                    withBorder 
                    className="markdown-viewer"
                    style={{ 
                        minHeight: '100px', 
                        cursor: disabled ? 'default' : 'text',
                        backgroundColor: 'var(--mantine-color-gray-0)'
                    }}
                    onClick={() => {
                        if (!disabled && onToggleEdit) onToggleEdit();
                    }}
                >
                    {value ? (
                        <div>
                            <ReactMarkdown 
                                remarkPlugins={[remarkGfm]}
                                components={components}
                            >
                                {value}
                            </ReactMarkdown>
                        </div>
                    ) : (
                        <Text c="dimmed" size="sm">No description provided. Click to add one.</Text>
                    )}
                </Paper>
            </Stack>
        );
    }

    return (
        <Stack gap="xs">
            <Paper p="xs" withBorder bg="var(--mantine-color-gray-0)">
                <Group justify="space-between">
                    <Group gap={4}>
                        {formatButtons.map((btn, idx) => (
                            <ActionIcon
                                key={idx}
                                variant="subtle"
                                color="gray"
                                onClick={btn.action}
                                title={btn.label}
                            >
                                <btn.icon size={16} />
                            </ActionIcon>
                        ))}
                    </Group>
                    {onToggleEdit && (
                        <Button variant="subtle" size="xs" onClick={onToggleEdit}>
                            Finish Editing
                        </Button>
                    )}
                </Group>
            </Paper>

            <Textarea
                data-markdown-editor="true"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onFocus={onFocus}
                onBlur={onBlur}
                placeholder={placeholder}
                minRows={minRows}
                autosize
                disabled={disabled}
                styles={{
                    input: {
                        fontFamily: 'var(--mantine-font-family-monospace)',
                        fontSize: '14px',
                        lineHeight: '1.6'
                    }
                }}
            />
        </Stack>
    );
};

export default MarkdownEditor;
